import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../config/firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // console.log('[AUTH] Auth state changed:', { hasUser: !!user });
      setUser(user);
      if (user) {
        // Get the ID token
        const idToken = await user.getIdToken();
        // console.log('[AUTH] Got new token');
        setToken(idToken);
        localStorage.setItem('firebaseToken', idToken);
      } else {
        setToken(null);
        localStorage.removeItem('firebaseToken');
      }
      setLoading(false);
    });

    // Setup token refresh
    const tokenRefreshInterval = setInterval(async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const newToken = await currentUser.getIdToken(true);
        // console.log('[AUTH] Token refreshed');
        setToken(newToken);
        localStorage.setItem('firebaseToken', newToken);
      }
    }, 30 * 60 * 1000); // Refresh token every 30 minutes

    return () => {
      unsubscribe();
      clearInterval(tokenRefreshInterval);
    };
  }, []);

  const login = async (email, password) => {
    try {
      // console.log('[AUTH] Attempting login...');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      // console.log('[AUTH] Login successful, token obtained');
      setToken(idToken);
      localStorage.setItem('firebaseToken', idToken);
      return userCredential;
    } catch (error) {
      console.error('[AUTH] Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // console.log('[AUTH] Logging out...');
      await signOut(auth);
      setToken(null);
      localStorage.removeItem('firebaseToken');
      // console.log('[AUTH] Logout successful');
    } catch (error) {
      console.error('[AUTH] Logout error:', error);
      throw error;
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    token
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
